import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useTranslation } from 'react-i18next';

interface Command {
  name: string;
  label: string;
  description: string;
  category: 'clear' | 'cache' | 'optimize';
  icon: string;
}

const ArtisanCommands: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [output, setOutput] = useState<string>('');
  const [success, setSuccess] = useState<boolean | null>(null);
  const [token, setToken] = useState<string>('');
  const [availableCommands, setAvailableCommands] = useState<string[]>([]);

  const commands: Command[] = [
    {
      name: 'cache:clear',
      label: 'Clear Application Cache',
      description: 'Clear all application cache',
      category: 'clear',
      icon: 'fas fa-trash-alt'
    },
    {
      name: 'config:clear',
      label: 'Clear Config Cache',
      description: 'Clear configuration cache',
      category: 'clear',
      icon: 'fas fa-cog'
    },
    {
      name: 'route:clear',
      label: 'Clear Route Cache',
      description: 'Clear route cache (useful after route changes)',
      category: 'clear',
      icon: 'fas fa-route'
    },
    {
      name: 'view:clear',
      label: 'Clear View Cache',
      description: 'Clear compiled view files',
      category: 'clear',
      icon: 'fas fa-eye'
    },
    {
      name: 'optimize:clear',
      label: 'Clear All Caches',
      description: 'Clear all cached files (recommended)',
      category: 'optimize',
      icon: 'fas fa-broom'
    },
    {
      name: 'config:cache',
      label: 'Cache Configuration',
      description: 'Cache configuration files for better performance',
      category: 'cache',
      icon: 'fas fa-save'
    },
    {
      name: 'route:cache',
      label: 'Cache Routes',
      description: 'Cache routes for better performance',
      category: 'cache',
      icon: 'fas fa-route'
    },
    {
      name: 'view:cache',
      label: 'Cache Views',
      description: 'Cache compiled views for better performance',
      category: 'cache',
      icon: 'fas fa-eye'
    },
    {
      name: 'optimize',
      label: 'Optimize Application',
      description: 'Cache configuration and routes for production',
      category: 'optimize',
      icon: 'fas fa-rocket'
    }
  ];

  useEffect(() => {
    // Load token from localStorage or prompt user
    const savedToken = localStorage.getItem('artisan_api_token');
    if (savedToken) {
      setToken(savedToken);
      fetchCommands();
    }
  }, []);

  const fetchCommands = async () => {
    try {
      const response = await api.get(`/artisan/commands?token=${token}`);
      if (response.data.success) {
        setAvailableCommands(response.data.commands);
      }
    } catch (error: any) {
      console.error('Error fetching commands:', error);
    }
  };

  const runCommand = async (commandName: string) => {
    if (!token) {
      alert('Please enter your Artisan API Token first');
      return;
    }

    setLoading(true);
    setOutput('');
    setSuccess(null);

    try {
      const response = await api.post('/artisan/run', {
        command: commandName,
        token: token
      });

      if (response.data.success) {
        setSuccess(true);
        setOutput(response.data.output || response.data.message);
      } else {
        setSuccess(false);
        setOutput(response.data.message || 'Command failed');
      }
    } catch (error: any) {
      setSuccess(false);
      setOutput(
        error.response?.data?.message || 
        error.message || 
        'Error executing command'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleTokenSave = () => {
    localStorage.setItem('artisan_api_token', token);
    if (token) {
      fetchCommands();
    }
    alert('Token saved!');
  };

  const getCommandsByCategory = (category: string) => {
    return commands.filter(cmd => cmd.category === category);
  };

  return (
    <div className="artisan-commands">
      <div className="admin-header">
        <h1><i className="fas fa-terminal"></i> Artisan Commands</h1>
        <p>Run Laravel artisan commands directly from the admin panel</p>
      </div>

      <div className="artisan-content">
        {/* Token Input Section */}
        <div className="artisan-section">
          <div className="section-header">
            <h2><i className="fas fa-key"></i> API Token</h2>
          </div>
          <div className="token-input-group">
            <input
              type="password"
              placeholder="Enter ARTISAN_API_TOKEN from .env file"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              className="token-input"
            />
            <button onClick={handleTokenSave} className="btn btn-primary">
              <i className="fas fa-save"></i> Save Token
            </button>
          </div>
          <p className="help-text">
            <i className="fas fa-info-circle"></i> 
            The token is stored locally in your browser. Make sure to set ARTISAN_API_TOKEN in your .env file.
          </p>
        </div>

        {/* Clear Commands */}
        <div className="artisan-section">
          <div className="section-header">
            <h2><i className="fas fa-trash-alt"></i> Clear Cache Commands</h2>
          </div>
          <div className="commands-grid">
            {getCommandsByCategory('clear').map((cmd) => (
              <div key={cmd.name} className="command-card">
                <div className="command-icon">
                  <i className={cmd.icon}></i>
                </div>
                <div className="command-info">
                  <h3>{cmd.label}</h3>
                  <p>{cmd.description}</p>
                  <code className="command-name">{cmd.name}</code>
                </div>
                <button
                  onClick={() => runCommand(cmd.name)}
                  disabled={loading || !token}
                  className="btn btn-primary"
                >
                  {loading ? (
                    <><i className="fas fa-spinner fa-spin"></i> Running...</>
                  ) : (
                    <><i className="fas fa-play"></i> Run</>
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Optimize Commands */}
        <div className="artisan-section">
          <div className="section-header">
            <h2><i className="fas fa-rocket"></i> Optimize Commands</h2>
          </div>
          <div className="commands-grid">
            {getCommandsByCategory('optimize').map((cmd) => (
              <div key={cmd.name} className="command-card">
                <div className="command-icon">
                  <i className={cmd.icon}></i>
                </div>
                <div className="command-info">
                  <h3>{cmd.label}</h3>
                  <p>{cmd.description}</p>
                  <code className="command-name">{cmd.name}</code>
                </div>
                <button
                  onClick={() => runCommand(cmd.name)}
                  disabled={loading || !token}
                  className="btn btn-success"
                >
                  {loading ? (
                    <><i className="fas fa-spinner fa-spin"></i> Running...</>
                  ) : (
                    <><i className="fas fa-play"></i> Run</>
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Cache Commands */}
        <div className="artisan-section">
          <div className="section-header">
            <h2><i className="fas fa-save"></i> Cache Commands</h2>
          </div>
          <div className="commands-grid">
            {getCommandsByCategory('cache').map((cmd) => (
              <div key={cmd.name} className="command-card">
                <div className="command-icon">
                  <i className={cmd.icon}></i>
                </div>
                <div className="command-info">
                  <h3>{cmd.label}</h3>
                  <p>{cmd.description}</p>
                  <code className="command-name">{cmd.name}</code>
                </div>
                <button
                  onClick={() => runCommand(cmd.name)}
                  disabled={loading || !token}
                  className="btn btn-info"
                >
                  {loading ? (
                    <><i className="fas fa-spinner fa-spin"></i> Running...</>
                  ) : (
                    <><i className="fas fa-play"></i> Run</>
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Output Section */}
        {(output || success !== null) && (
          <div className="artisan-section">
            <div className="section-header">
              <h2>
                <i className={`fas ${success ? 'fa-check-circle' : 'fa-times-circle'}`}></i> 
                Command Output
              </h2>
            </div>
            <div className={`command-output ${success ? 'success' : 'error'}`}>
              <pre>{output || 'No output'}</pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArtisanCommands;

